import { Card, CardBody, Skeleton } from "@heroui/react";
import { useStats } from "@src/presentation/hooks/stats";
import { Users, UserCheck, UserX, ShieldCheck, Shield } from "lucide-react";
import { usePermission } from "@src/presentation/hooks/usePermission";
import { PermissionPlaceholder } from "@src/presentation/components/PermissionPlaceholder";

export function UserStatsCard() {
    const { hasPermission } = usePermission();
    const canViewStats = hasPermission("stats:read");

    const { data: stats, isLoading, error } = useStats({
        refetchInterval: 30000,
        staleTime: 10000,
        enabled: canViewStats,
    });

    // Early return if no permission
    if (!canViewStats) {
        return <PermissionPlaceholder title="User Statistics" description="You need 'stats:read' permission to view user statistics." />;
    }

    if (isLoading) {
        return (
            <Card className="border border-border rounded shadow-none">
                <CardBody className="p-6">
                    <Skeleton className="h-6 w-32 rounded mb-4" />
                    <div className="space-y-3">
                        <Skeleton className="h-16 w-full rounded" />
                        <Skeleton className="h-16 w-full rounded" />
                        <Skeleton className="h-16 w-full rounded" />
                    </div>
                </CardBody>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="border border-border rounded shadow-none">
                <CardBody className="p-6">
                    <p className="text-sm text-destructive">Failed to load user stats</p>
                </CardBody>
            </Card>
        );
    }

    const userStats = [
        {
            label: "Total Users",
            value: stats?.dashboard.total_users ?? 0,
            icon: Users,
            color: "primary",
            bgColor: "bg-primary/10",
            textColor: "text-primary",
        },
        {
            label: "Active Users",
            value: stats?.dashboard.active_users ?? 0,
            icon: UserCheck,
            color: "success",
            bgColor: "bg-success/10",
            textColor: "text-success",
        },
        {
            label: "Verified Users",
            value: stats?.dashboard.verified_users ?? 0,
            icon: ShieldCheck,
            color: "success",
            bgColor: "bg-success/10",
            textColor: "text-success",
        },
        {
            label: "Banned Users",
            value: stats?.dashboard.banned_users ?? 0,
            icon: UserX,
            color: "danger",
            bgColor: "bg-danger/10",
            textColor: "text-danger",
        },
        {
            label: "Users with Roles",
            value: stats?.dashboard.users_with_roles ?? 0,
            icon: Shield,
            color: "warning",
            bgColor: "bg-warning/10",
            textColor: "text-warning",
        },
        {
            label: "Users without Roles",
            value: stats?.dashboard.users_without_roles ?? 0,
            icon: UserX,
            color: "default",
            bgColor: "bg-default/10",
            textColor: "text-default-500",
        },
    ];

    return (
        <Card className="border border-border rounded shadow-none hover:shadow-custom transition-shadow">
            <CardBody className="p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded bg-primary/10">
                        <Users className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-foreground">User Statistics</h2>
                        <p className="text-sm text-muted-foreground">User breakdown and metrics</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {userStats.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <div
                                key={index}
                                className="flex items-center gap-3 p-3 rounded border border-border bg-background hover:border-primary/50 transition-colors"
                            >
                                <div className={`flex h-10 w-10 items-center justify-center rounded ${stat.bgColor}`}>
                                    <Icon className={`h-5 w-5 ${stat.textColor}`} />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                                    <p className={`text-xl font-bold ${stat.textColor}`}>{stat.value}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Progress Bars */}
                <div className="mt-6 space-y-3">
                    <div>
                        <div className="flex justify-between text-xs mb-1">
                            <span className="text-muted-foreground">Verification Rate</span>
                            <span className="font-medium">
                                {stats?.dashboard.total_users
                                    ? Math.round((stats.dashboard.verified_users / stats.dashboard.total_users) * 100)
                                    : 0}%
                            </span>
                        </div>
                        <div className="h-2 bg-default-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-success rounded-full transition-all"
                                style={{
                                    width: `${stats?.dashboard.total_users
                                        ? (stats.dashboard.verified_users / stats.dashboard.total_users) * 100
                                        : 0}%`,
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="flex justify-between text-xs mb-1">
                            <span className="text-muted-foreground">Role Assignment Rate</span>
                            <span className="font-medium">
                                {stats?.dashboard.total_users
                                    ? Math.round((stats.dashboard.users_with_roles / stats.dashboard.total_users) * 100)
                                    : 0}%
                            </span>
                        </div>
                        <div className="h-2 bg-default-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-warning rounded-full transition-all"
                                style={{
                                    width: `${stats?.dashboard.total_users
                                        ? (stats.dashboard.users_with_roles / stats.dashboard.total_users) * 100
                                        : 0}%`,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
}
